import {
  type ChangeAcknowledgement,
  acknowledgementOf,
  type ChangeConsequences,
  type DraftArtifactKind,
  MAX_RQ_TEXT_CHARS,
  RQS_DRAFT_POLL_MS,
  RQS_DRAFT_WAIT_MS,
  type ReconciliationItem,
  STALE_UPSTREAM_KINDS,
  canonicalJson,
  rqsDraftNeeded,
  rqsDraftRequestNeeded,
  rqsReconcileNeeded,
  rqsVersionReasonText,
  sameRqsDraftInputs,
} from '@sigora/domain';
import { useCallback, useEffect, useRef, useState } from 'react';

import {
  type QuestionsView,
  approveArtifact,
  fetchQuestions,
  requestQuestionsDraft,
  rewordQuestion,
} from '../api/client.ts';
import { domainCodeOf } from '../api/errors.ts';
import { STATUS_LABELS } from '../artifacts/state.ts';
import { ChangeConfirm, unacknowledgedChangeOf } from './ChangeConsequences.tsx';
import {
  QUESTIONS_DOCUMENT,
  QUESTIONS_DRAFT_NEEDS_MATERIALS,
  QUESTIONS_NEED_MATERIALS,
  questionsReconciledText,
} from './questions-copy.ts';
import {
  BEFORE_YOUR_CHANGE,
  CHANGE_MARK_LABELS,
  RECONCILED_REMOVED_HEADING,
  reconcileFailedText,
  reconcileOverdueText,
  reconcilePendingText,
  reconciledUnchangedText,
} from './reconcile-copy.ts';
import { Pending } from '../ui/Pending.tsx';
import {
  APPROVING_LABEL,
  EDITS_OPEN_HINT,
  QUESTIONS_DRAFT_FAILED_HINT,
  QUESTIONS_DRAFTING_HINT,
  QUESTIONS_NEED_GOAL_HINT,
  QUESTIONS_RETRY_HINT,
  QUESTIONS_UPDATING_HINT,
  SAVING_HINT,
} from './continue-copy.ts';
import { type FirstDraft, firstDraftOf } from './first-draft.ts';
import { useRefusalFocus } from './refusal-focus.ts';
import { useContinueAction } from './step-footer.tsx';
import { VersionHistory, historyEntriesOf } from './VersionHistory.tsx';

export interface QuestionsStepProps {
  studyId: string;
  /**
   * Whether the study's inputs are fixed: paid for, frozen or launched. The step is then shown as
   * it stands, asking for nothing and offering no edit or approval the server would refuse.
   */
  frozen: boolean;
  /** Bumped by the shell whenever the study may have changed; the step is read again on it. */
  refreshToken: number;
  /** Tells the shell the questions changed, so the steps and the sheet are read again. */
  onStudyChanged: () => void;
  /** Hands the shell the turn an approval started, which drafts what comes next. */
  onApproved: (approved: { kind: DraftArtifactKind; turnId: string | null }) => void;
}

/** What the step says when what it showed moved under an edit or an approval. */
const CHANGED_NOTICE = 'These questions changed; look again.';

/**
 * The refusals that mean the questions on screen are no longer the ones the server holds. A reword
 * is refused only the first two; `artifact_stale` is the approval's answer to a Goal change that
 * landed after the render. Both a reword and an approval read the step again on any of them.
 */
const MOVED_CODES = new Set([
  'artifact_version_not_current',
  'rqs_goal_outdated',
  'artifact_stale',
]);

const failureMessage = (failure: unknown, fallback: string): string =>
  failure instanceof Error ? failure.message : fallback;

interface PendingSince {
  requestId: string;
  at: number;
}

/** When the step started waiting on this request: the first time it saw it pending. */
const startWaiting = (since: { current: PendingSince | null }, requestId: string): number => {
  if (since.current?.requestId !== requestId) {
    since.current = { requestId, at: Date.now() };
  }

  return since.current.at;
};

/**
 * The Questions step: the research questions Sigora drafted from the chosen Goal, one card each
 * with the part of the Goal's decision it informs. The footer's Continue approves them.
 *
 * Everything shown is the server's: the questions, the request behind them, and every version with
 * what produced it. So a refresh restores the step by reading it again, and nothing here decides
 * what the server will accept — a reword and an approval both name the version on screen, and the
 * server refuses either over questions that moved.
 *
 * The researcher changes the wording of one question at a time, and may have several edits open;
 * approval waits until none is. Adding or removing a question is asked for in the step's
 * conversation, whose turns end with the step read again.
 *
 * The questions are asked for on arrival, once per set of inputs, when the study holds none for its
 * chosen Goal — none at all, or a set its Goal changed under — and the step then polls until the
 * request settles or the wait runs out. "Try again" asks regardless. Questions waiting only on
 * changed Materials are reconciled the same way: the step shows them as they were before the change
 * while the reconciliation is pending, says so when it fails, and once it lands marks each question
 * it changed or added with the reason it gave, until the questions are approved.
 */
export const QuestionsStep = ({
  studyId,
  frozen,
  refreshToken,
  onStudyChanged,
  onApproved,
}: QuestionsStepProps) => {
  const [view, setView] = useState<QuestionsView | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  /** Why the step was read again under the reader, said once over it. */
  const [notice, setNotice] = useState<string | null>(null);
  const [requestError, setRequestError] = useState<string | null>(null);
  const [asking, setAsking] = useState(false);
  const [approving, setApproving] = useState(false);
  const [approveError, setApproveError] = useState<string | null>(null);
  /** Every open edit: the question's id and the wording typed so far. */
  const [edits, setEdits] = useState<ReadonlyMap<string, string>>(new Map());
  /** The question whose reword is out. */
  const [saving, setSaving] = useState<string | null>(null);
  /**
   * A reword or an approval has landed and the read that follows it has not. The step then holds
   * every save and the approval: the set on screen is the one the write replaced, so a save or an
   * approval made over it would quote a version that moved, or approve the same version twice.
   */
  const [settling, setSettling] = useState(false);
  /**
   * The first read that can settle a write: the one it started, or any newer one. An older read
   * that lands after the write, superseded, says nothing about what it wrote.
   */
  const settlesFrom = useRef<number | null>(null);
  /** What the server refused a reword with, on the card it was made on. */
  const [editErrors, setEditErrors] = useState<ReadonlyMap<string, string>>(new Map());
  /**
   * The reword waiting on the reader's say-so, with what the server said it would do below the
   * questions. What they typed stays open under it either way.
   */
  const [confirming, setConfirming] = useState<{
    rqId: string;
    consequences: ChangeConsequences;
  } | null>(null);

  /** The request that stopped being waited on, so a stale deadline cannot silence a new one. */
  const [waitedOut, setWaitedOut] = useState<string | null>(null);
  const pendingSince = useRef<PendingSince | null>(null);
  /** Counts the poll's ticks, so each tick arms the next whatever the read it asked for returned. */
  const [tick, setTick] = useState(0);

  /**
   * Only the newest answer is shown. A poll's read and a request's answer can cross, and the older
   * landing last would put back questions or a request the reader has just moved past.
   */
  const latest = useRef(0);
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;

    return () => {
      alive.current = false;
    };
  }, []);

  const editButtons = useRef(new Map<string, HTMLButtonElement>());
  const editFields = useRef(new Map<string, HTMLTextAreaElement>());
  /** The question an edit was just opened on, whose field takes the focus. */
  const [focusField, setFocusField] = useState<string | null>(null);
  /** The question an edit was just closed on, whose Edit button takes the focus back. */
  const [returnFocusTo, setReturnFocusTo] = useState<string | null>(null);

  /** Something is in flight that a reword or an approval must not race. */
  const busy = asking || approving || saving !== null || settling;

  const issue = () => {
    latest.current += 1;

    return latest.current;
  };

  const load = useCallback(
    async (signal?: AbortSignal) => {
      latest.current += 1;
      const ticket = latest.current;
      /** The newest read has landed, one way or the other, so a reword waiting on it is settled. */
      const settle = () => {
        if (settlesFrom.current !== null && ticket >= settlesFrom.current) {
          settlesFrom.current = null;
          setSettling(false);
        }
      };

      try {
        const next = await fetchQuestions(studyId, signal);
        if (alive.current && ticket === latest.current) {
          setView(next);
          setLoadError(null);
          // A request that failed on the way stops mattering once nothing needs drafting or
          // reconciling.
          if (!rqsDraftNeeded(next.rqs) && !rqsReconcileNeeded(next.rqs)) {
            setRequestError(null);
          }
          settle();
        }
      } catch (failure: unknown) {
        // A read given up on is not a read that failed: the deadline that aborted it says so itself.
        if (alive.current && ticket === latest.current && signal?.aborted !== true) {
          setLoadError(failureMessage(failure, 'Your questions could not be read.'));
          settle();
        }
      }
    },
    [studyId],
  );

  useEffect(() => {
    void load();
  }, [load, refreshToken]);

  /** Holds the step after a write until a read started now, or a newer one, has landed. */
  const holdUntilRead = () => {
    setSettling(true);
    // The read this starts is the next ticket; any newer one settles the write as well.
    settlesFrom.current = latest.current + 1;
    void load();
  };

  /**
   * Asks for a draft, shows the request the server stored, and then reads the whole step again. The
   * read is what makes the answer stick: a read started while the request was in flight — the
   * shell's own, after it records the step — can have looked before the request committed and still
   * land last, and the request is judged against the study's current inputs, which only a read of
   * the step brings up to date.
   */
  const ask = useCallback(
    (again: boolean) => {
      setAsking(true);
      setRequestError(null);
      const ticket = issue();

      void requestQuestionsDraft(studyId, { again }).then(
        (draft) => {
          setAsking(false);
          if (alive.current && ticket === latest.current && draft !== null) {
            setView((current) => (current === null ? current : { ...current, draft }));
          }
          void load();
        },
        (failure: unknown) => {
          setAsking(false);
          if (alive.current) {
            setRequestError(failureMessage(failure, 'Sigora could not be asked for questions.'));
          }
          void load();
        },
      );
    },
    [studyId, load],
  );

  const rqs = view?.rqs ?? null;
  const currentInputs = view?.currentInputs ?? null;
  /** No questions yet, or a set its Goal changed under: either is only ever drafted, not edited. */
  const draftNeeded = view !== null && rqsDraftNeeded(rqs);
  /** Questions waiting only on changed Materials, which are reconciled rather than edited. */
  const reconciling = view !== null && rqsReconcileNeeded(rqs);
  /** What the questions are waiting on, in the order a change travels. */
  const staleKinds = STALE_UPSTREAM_KINDS.filter(
    (kind) => rqs?.staleness.some((window) => window.upstream_kind === kind) ?? false,
  );
  const draft = view?.draft ?? null;
  const forNow =
    draft !== null && currentInputs !== null && sameRqsDraftInputs(draft.inputs, currentInputs)
      ? draft
      : null;
  const pendingId = forNow?.status === 'pending' ? forNow.id : null;
  const waiting = pendingId !== null && waitedOut !== pendingId;

  /**
   * Arrival: ask once for the study's inputs when a draft or a reconciliation is needed and no
   * request for those inputs is still worth waiting on. Keyed on the inputs, so a Goal chosen or
   * Materials confirmed again while the step is open asks again, and a request that failed is not
   * repeated on every read; "Try again" is how it is asked for again.
   */
  const askedFor = useRef<string | null>(null);
  useEffect(() => {
    if (view === null || currentInputs === null || frozen) {
      return;
    }

    const key = canonicalJson(currentInputs);
    if (askedFor.current === key) {
      return;
    }

    askedFor.current = key;
    if (
      rqsDraftRequestNeeded({
        request: view.draft,
        inputs: currentInputs,
        rqs: view.rqs,
        now: new Date(),
      })
    ) {
      ask(false);
    }
  }, [view, currentInputs, frozen, ask]);

  /** The poll's read in flight, which the deadline abandons rather than waits on. */
  const pollRead = useRef<AbortController | null>(null);
  useEffect(
    () => () => {
      pollRead.current?.abort();
    },
    [],
  );

  /**
   * The deadline, on a timer of its own, armed once for each request waited on: a read that never
   * answers would otherwise hold the step pending for good, since the poll asks again only once its
   * read has answered.
   */
  useEffect(() => {
    if (pendingId === null || waitedOut === pendingId) {
      return undefined;
    }

    const since = startWaiting(pendingSince, pendingId);
    const deadline = setTimeout(
      () => {
        pollRead.current?.abort();
        setWaitedOut(pendingId);
      },
      Math.max(0, since + RQS_DRAFT_WAIT_MS - Date.now()),
    );

    return () => clearTimeout(deadline);
  }, [pendingId, waitedOut]);

  /**
   * One timer at a time, and none once the request has settled or the wait has run out. Each tick
   * arms the next only once its read has answered, so at most one read is in flight. Torn down with
   * the render that armed it, so nothing is left ticking when the step moves on.
   */
  useEffect(() => {
    if (pendingId === null || waitedOut === pendingId) {
      return undefined;
    }

    const since = startWaiting(pendingSince, pendingId);
    const overdue = () => Date.now() - since >= RQS_DRAFT_WAIT_MS;
    if (overdue()) {
      setWaitedOut(pendingId);
      return undefined;
    }

    const timer = setTimeout(() => {
      if (overdue()) {
        setWaitedOut(pendingId);
        return;
      }

      const controller = new AbortController();
      pollRead.current = controller;
      void load(controller.signal).then(() => {
        if (alive.current && !controller.signal.aborted) {
          setTick((count) => count + 1);
        }
      });
    }, RQS_DRAFT_POLL_MS);

    return () => clearTimeout(timer);
  }, [pendingId, waitedOut, load, tick]);

  useEffect(() => {
    if (focusField !== null) {
      editFields.current.get(focusField)?.focus();
      setFocusField(null);
    }
  }, [focusField]);

  useEffect(() => {
    if (returnFocusTo !== null && !edits.has(returnFocusTo)) {
      editButtons.current.get(returnFocusTo)?.focus();
      setReturnFocusTo(null);
    }
  }, [returnFocusTo, edits]);

  const setEditError = (rqId: string, message: string | null) => {
    setEditErrors((errors) => {
      const next = new Map(errors);
      if (message === null) {
        next.delete(rqId);
      } else {
        next.set(rqId, message);
      }

      return next;
    });
  };

  const closeEdit = (rqId: string) => {
    setEdits((open) => {
      const next = new Map(open);
      next.delete(rqId);

      return next;
    });
    setEditError(rqId, null);
    setConfirming((current) => (current?.rqId === rqId ? null : current));
    setReturnFocusTo(rqId);
  };

  const openEdit = (rqId: string, text: string) => {
    setEdits((open) => new Map(open).set(rqId, text));
    setEditError(rqId, null);
    setFocusField(rqId);
  };

  /** A refusal that says the questions on screen moved: say so once, and read them again. */
  const moved = () => {
    setNotice(CHANGED_NOTICE);
    void load();
    onStudyChanged();
  };

  const save = (rqId: string, acknowledgedDownstream: ChangeAcknowledgement | null = null) => {
    const text = edits.get(rqId);
    if (rqs === null || text === undefined || busy) {
      return;
    }

    setSaving(rqId);
    setEditError(rqId, null);
    setConfirming(null);
    setNotice(null);

    void rewordQuestion({
      studyId,
      rqId,
      text,
      expectedVersion: rqs.version,
      acknowledgedDownstream,
    }).then(
      ({ version, deduped }) => {
        setSaving(null);
        closeEdit(rqId);
        // The reword on screen at once, in the researcher's words and at the version it wrote, so
        // nothing flickers back to the old wording while the read that confirms it is out.
        setView((current) => {
          if (current?.rqs == null || deduped) {
            return current;
          }

          return {
            ...current,
            // The version on screen is the researcher's rewording now, not a reconciled one.
            reconciliation: null,
            rqs: {
              ...current.rqs,
              version,
              status: 'unapproved',
              content: {
                ...current.rqs.content,
                items: current.rqs.content.items.map((item) =>
                  item.id === rqId ? { ...item, text, worded_by: 'user' } : item,
                ),
              },
            },
            history: [
              ...current.history,
              {
                version,
                authoredBy: 'user',
                reason: 'reworded_by_you',
                reasonText: rqsVersionReasonText({
                  reason: 'reworded_by_you',
                  previous: null,
                  current: current.rqs.content,
                }),
                createdAt: new Date().toISOString(),
              },
            ],
          };
        });
        holdUntilRead();
        onStudyChanged();
      },
      (failure: unknown) => {
        setSaving(null);
        const consequences = unacknowledgedChangeOf(failure);
        if (consequences !== null) {
          setConfirming({ rqId, consequences });
          return;
        }

        const code = domainCodeOf(failure);

        if (code !== null && MOVED_CODES.has(code)) {
          // What was typed stays open over the questions as they are now, and saving it again names
          // them; questions a Goal change left waiting are only ever redrafted, so no edit is drawn.
          moved();
          return;
        }

        if (code === 'rq_unknown') {
          closeEdit(rqId);
          moved();
          return;
        }

        setEditError(rqId, failureMessage(failure, 'That wording could not be saved.'));
      },
    );
  };

  /** Approves the questions on screen, from Continue; true once accepted. */
  const approve = (): Promise<boolean> => {
    if (rqs === null || busy) {
      return Promise.resolve(false);
    }

    setApproving(true);
    setApproveError(null);
    setNotice(null);

    return approveArtifact({ studyId, artifactId: rqs.artifactId, version: rqs.version }).then(
      ({ turnId }) => {
        setApproving(false);
        onApproved({ kind: 'rqs', turnId });
        holdUntilRead();
        onStudyChanged();
        return true;
      },
      (failure: unknown) => {
        setApproving(false);
        const code = domainCodeOf(failure);

        if (code !== null && MOVED_CODES.has(code)) {
          moved();
          return false;
        }

        setApproveError(failureMessage(failure, 'These questions could not be approved.'));
        if (code !== null) {
          void load();
        }
        return false;
      },
    );
  };

  /**
   * The questions as they stand can be reworded and approved: current against the chosen Goal and
   * waiting on nothing above them.
   */
  const editable = !frozen && rqs !== null && !draftNeeded && !reconciling;
  /**
   * The edits still open over the questions on screen. An edit of a question a read no longer
   * carries — removed in the step's conversation, or redrafted — is gone with it, and holds up
   * nothing; ids are minted fresh, so it cannot come back.
   */
  const openEdits = editable ? rqs.content.items.filter((item) => edits.has(item.id)).length : 0;
  const failed = forNow?.status === 'unavailable' || forNow?.status === 'unreadable';
  const overdue = pendingId !== null && !waiting;
  /**
   * A request refused or lost on the way stores nothing, so there is no failed request to show; the
   * refusal on screen is the only trace of it, and the retry is offered beside it.
   */
  const retryOffered =
    !frozen &&
    currentInputs !== null &&
    (draftNeeded || reconciling) &&
    (failed || overdue || requestError !== null);
  /**
   * What above the questions a draft or an update waits on, which only the reader can settle: no
   * chosen Goal, or Materials changed and not confirmed since. Nothing can be asked until then.
   */
  const waitsOn: 'goal' | 'materials' | null =
    view === null || frozen || currentInputs !== null || !(draftNeeded || reconciling)
      ? null
      : view.goal === null
        ? 'goal'
        : 'materials';
  /** What the step says of Materials it waits on, for questions drafted or not yet drafted. */
  const needsMaterials = rqs === null ? QUESTIONS_DRAFT_NEEDS_MATERIALS : QUESTIONS_NEED_MATERIALS;
  const firstDraft: FirstDraft<'goal' | 'materials'> | null = firstDraftOf({
    read: view !== null,
    drafted: rqs !== null,
    waitsOn,
    failed: retryOffered,
  });

  /**
   * What holds the approval of the questions on screen, first what the reader can act on, or null
   * when nothing does: Continue approves on exactly that. With no questions drafted yet it names
   * what the first draft waits on, as the step's own lines do.
   */
  const approvalBlock = (): string | null => {
    if (waitsOn !== null) {
      return waitsOn === 'goal' ? QUESTIONS_NEED_GOAL_HINT : needsMaterials;
    }

    if (firstDraft !== null) {
      return firstDraft.status === 'failed' ? QUESTIONS_DRAFT_FAILED_HINT : QUESTIONS_DRAFTING_HINT;
    }

    if (retryOffered) {
      return QUESTIONS_RETRY_HINT;
    }

    if (!editable || rqs.status !== 'unapproved' || asking) {
      return QUESTIONS_UPDATING_HINT;
    }

    if (openEdits > 0) {
      return EDITS_OPEN_HINT;
    }

    return approving || saving !== null || settling ? SAVING_HINT : null;
  };
  const blocked = approvalBlock();
  const approvable = blocked === null;

  const { refusal, continueRun } = useRefusalFocus();
  /**
   * Continue approves questions not yet approved, and stays on the step over a refusal, which it
   * takes the reader to. Before any are drafted it is held with what the draft waits on. Approved
   * or fixed questions leave Continue a plain move, and so does the first read still out.
   */
  useContinueAction(
    view === null || frozen || rqs?.status === 'approved'
      ? null
      : {
          run: continueRun(approve),
          disabled: !approvable,
          blockedHint: blocked,
          busyLabel: APPROVING_LABEL,
        },
  );

  if (view === null) {
    return loadError === null ? (
      <p data-test-id="questions-loading">Reading your questions…</p>
    ) : (
      <p className="chat-error" data-test-id="questions-error" role="alert">
        {loadError}{' '}
        <button type="button" data-test-id="questions-reload" onClick={() => void load()}>
          Try again
        </button>
      </p>
    );
  }

  const reasonShown =
    rqs === null
      ? ''
      : (view.history.find((entry) => entry.version === rqs.version)?.reasonText ?? '');
  /**
   * What the reconciliation behind the questions on screen changed, shown until they are approved:
   * the history keeps the version's line after that.
   */
  const reconciled =
    view.reconciliation !== null &&
    rqs !== null &&
    view.reconciliation.version === rqs.version &&
    rqs.status === 'unapproved'
      ? view.reconciliation
      : null;
  // A removed question's id is no card on screen, so only what changed or was added is ever found.
  const marks = new Map<string, ReconciliationItem>(
    (reconciled?.items ?? []).map((item) => [item.id, item]),
  );
  const removedItems = reconciled?.items.filter((item) => item.change === 'removed') ?? [];

  return (
    <div className="questions" data-test-id="questions">
      <h2 className="questions-heading">
        These are the questions your study will answer. Edit any wording, then press Continue to
        approve them.
      </h2>

      {notice !== null && (
        <p
          ref={refusal}
          className="questions-notice"
          data-test-id="questions-notice"
          role="status"
          tabIndex={-1}
        >
          {notice}
        </p>
      )}

      {/*
        A read that failed over a step already on screen: what is shown is the last read that
        answered, so the step says it may be out of date rather than passing it off as current.
      */}
      {loadError !== null && (
        <p className="chat-error" data-test-id="questions-refresh-error" role="alert">
          Your questions could not be read again, so this may be out of date. {loadError}{' '}
          <button type="button" data-test-id="questions-refresh-retry" onClick={() => void load()}>
            Try again
          </button>
        </p>
      )}

      {waitsOn === 'goal' && (
        <p className="questions-status" data-test-id="questions-draft-needs-goal">
          Choose your goal first. Sigora drafts the questions from it.
        </p>
      )}

      {/*
        A study with a chosen Goal and nothing to draft or reconcile the questions from: the
        Materials changed and are not confirmed yet, so nothing is asked.
      */}
      {waitsOn === 'materials' && (
        <p className="questions-status" data-test-id="questions-needs-materials">
          {needsMaterials}
        </p>
      )}

      {/*
        Only while a redraft is being asked for or waited on: beside a draft that failed or ran
        late, the lines below say what happened instead. A set that names no Goal was never changed
        under, so it is only said to be redrafted.
      */}
      {rqs !== null && draftNeeded && (asking || waiting) && !frozen && (
        <p className="questions-notice" data-test-id="questions-draft-redrafting">
          {rqs.content.goal === null
            ? 'Sigora is redrafting these questions from your goal.'
            : 'Your goal changed, so Sigora is redrafting these questions.'}
        </p>
      )}

      {reconciled !== null && !reconciled.unchanged && (
        <div className="questions-notice" data-test-id="questions-reconciled">
          <p>{questionsReconciledText(reconciled.upstreamKinds)}</p>
          {removedItems.length > 0 && (
            <>
              <p>{RECONCILED_REMOVED_HEADING}</p>
              <ul>
                {removedItems.map((item) => (
                  <li key={item.id} data-test-id="questions-reconciled-removed">
                    {item.label}
                    {item.reason !== null && (
                      <span className="questions-informs"> — {item.reason}</span>
                    )}
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}

      {reconciled?.unchanged === true && (
        <p className="questions-notice" data-test-id="questions-reconciled-unchanged">
          {reconciledUnchangedText(reconciled.upstreamKinds)}
        </p>
      )}

      <div aria-live="polite">
        {reconciling && !frozen && (asking || waiting) && (
          <Pending live={false} className="questions-status" testId="questions-reconcile-pending">
            {reconcilePendingText(QUESTIONS_DOCUMENT, staleKinds)}
          </Pending>
        )}

        {reconciling && overdue && (
          <p className="questions-status" data-test-id="questions-reconcile-overdue">
            {reconcileOverdueText(QUESTIONS_DOCUMENT)}
          </p>
        )}

        {reconciling && failed && (
          <p className="questions-status" data-test-id="questions-reconcile-failed">
            {reconcileFailedText(QUESTIONS_DOCUMENT)}
          </p>
        )}

        {!reconciling && waiting && (
          <Pending live={false} className="questions-status" testId="questions-draft-pending">
            Sigora is drafting your questions from your goal…
          </Pending>
        )}

        {!reconciling && overdue && (
          <p className="questions-status" data-test-id="questions-draft-overdue">
            Sigora is taking longer than it should to draft your questions.
          </p>
        )}

        {!reconciling && forNow?.status === 'unavailable' && (
          <p className="questions-status" data-test-id="questions-draft-unavailable">
            Sigora could not draft questions just now.
          </p>
        )}

        {!reconciling && forNow?.status === 'unreadable' && (
          <p className="questions-status" data-test-id="questions-draft-unreadable">
            Sigora’s draft did not come back as {view.intendedCount} complete questions.
          </p>
        )}
      </div>

      {retryOffered && (
        <button
          type="button"
          data-test-id="questions-retry"
          disabled={busy}
          onClick={() => ask(true)}
        >
          Try again
        </button>
      )}

      {requestError !== null && (
        <p className="chat-error" data-test-id="questions-request-error" role="alert">
          {requestError}
        </p>
      )}

      {rqs !== null && (
        <div className="card document-card">
          {/* The questions on screen are the ones the change came after, never passed off as current. */}
          {reconciling && (
            <p className="questions-status" data-test-id="questions-before-change">
              {BEFORE_YOUR_CHANGE}
            </p>
          )}

          <ol className="questions-list" data-test-id="questions-list">
            {rqs.content.items.map((item, index) => {
              const typed = editable ? edits.get(item.id) : undefined;
              const error = editErrors.get(item.id) ?? null;
              const fieldId = `rq-wording-${item.id}`;
              const mark = marks.get(item.id);

              return (
                <li
                  className="questions-card"
                  data-rq-id={item.id}
                  data-test-id="rq-card"
                  key={item.id}
                >
                  <p className="questions-number" aria-hidden="true">
                    Question {index + 1}
                  </p>
                  {typed === undefined ? (
                    <p className="questions-text" data-test-id="rq-text">
                      {item.text}
                    </p>
                  ) : (
                    <div
                      className="questions-edit"
                      onKeyDown={(event) => {
                        if (event.key === 'Escape' && saving !== item.id) {
                          event.preventDefault();
                          closeEdit(item.id);
                        }
                      }}
                    >
                      <label className="visually-hidden" htmlFor={fieldId}>
                        Wording
                      </label>
                      <textarea
                        id={fieldId}
                        data-test-id="rq-wording"
                        value={typed}
                        maxLength={MAX_RQ_TEXT_CHARS}
                        rows={3}
                        disabled={saving === item.id}
                        ref={(field) => {
                          if (field === null) {
                            editFields.current.delete(item.id);
                          } else {
                            editFields.current.set(item.id, field);
                          }
                        }}
                        onChange={(event) => {
                          const text = event.target.value;
                          setEdits((open) => new Map(open).set(item.id, text));
                          // The confirm was for the wording as it was sent.
                          setConfirming((current) => (current?.rqId === item.id ? null : current));
                        }}
                        onKeyDown={(event) => {
                          if (
                            event.key === 'Enter' &&
                            !event.shiftKey &&
                            !event.nativeEvent.isComposing
                          ) {
                            event.preventDefault();
                            if (typed.trim() !== '') {
                              save(item.id);
                            }
                          }
                        }}
                      />
                      <div className="questions-edit-actions">
                        <button
                          type="button"
                          data-test-id="rq-save"
                          disabled={busy || confirming?.rqId === item.id || typed.trim() === ''}
                          onClick={() => save(item.id)}
                        >
                          {saving === item.id ? 'Saving…' : 'Save'}
                        </button>
                        <button
                          type="button"
                          data-test-id="rq-cancel"
                          disabled={saving === item.id}
                          onClick={() => closeEdit(item.id)}
                        >
                          Cancel
                        </button>
                      </div>
                      {confirming?.rqId === item.id && (
                        <ChangeConfirm
                          consequences={confirming.consequences}
                          busy={busy}
                          onConfirm={() =>
                            save(item.id, acknowledgementOf(confirming.consequences))
                          }
                          onCancel={() => {
                            setConfirming(null);
                            setFocusField(item.id);
                          }}
                        />
                      )}
                      {error !== null && (
                        <p className="chat-error" data-test-id="rq-error" role="alert">
                          {error}
                        </p>
                      )}
                    </div>
                  )}

                  {item.decision !== '' && (
                    <p className="questions-informs" data-test-id="rq-informs">
                      Informs: {item.decision}
                    </p>
                  )}

                  {item.worded_by === 'user' && (
                    <span className="questions-tag" data-test-id="rq-your-wording">
                      Your wording
                    </span>
                  )}

                  {mark !== undefined && mark.change !== 'removed' && (
                    <>
                      <span className="questions-tag" data-test-id="rq-change-mark">
                        {CHANGE_MARK_LABELS[mark.change]}
                      </span>
                      <p className="questions-informs" data-test-id="rq-change-reason">
                        {mark.reason ?? reasonShown}
                      </p>
                    </>
                  )}

                  {editable && typed === undefined && (
                    <button
                      type="button"
                      className="questions-edit-open text-link muted"
                      data-test-id="rq-edit"
                      aria-label={`Edit question ${String(index + 1)}`}
                      ref={(button) => {
                        if (button === null) {
                          editButtons.current.delete(item.id);
                        } else {
                          editButtons.current.set(item.id, button);
                        }
                      }}
                      onClick={() => openEdit(item.id, item.text)}
                    >
                      Edit
                    </button>
                  )}
                </li>
              );
            })}
          </ol>

          <p className="questions-status" data-test-id="questions-status">
            {STATUS_LABELS[rqs.status]}
          </p>

          {approveError !== null && (
            <p
              ref={refusal}
              className="chat-error"
              data-test-id="questions-approve-error"
              role="alert"
              tabIndex={-1}
            >
              {approveError}
            </p>
          )}

          <VersionHistory
            testIdPrefix="questions"
            current={{ version: rqs.version, reasonText: reasonShown }}
            entries={historyEntriesOf(view.history)}
          />
        </div>
      )}
    </div>
  );
};
